import { useIsMobile } from '@/hooks/useIsMobile';
import { Skeleton } from '@/shared/ui/Skeleton';
import skeletonStyles from './AppShellSkeleton.module.css';

// WHY: the auth-bootstrap + lazy-route fallback shell (ProtectedLayout, route-loading-fallback,
// AppRouter). Below 767px the desktop sidebar shell was wrong — every mobile cold start flashed a
// squeezed desktop sidebar before hard-swapping to the strip (MOB-01/H1). Branch on useIsMobile
// (the canonical JS breakpoint switch — no second breakpoint mechanism) to a mobile-shaped
// placeholder that matches the shell 2.0 the strip hydrates into: identity-header bar -> content
// blocks -> bottom-nav bar.
export function AppShellSkeleton() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileShellSkeleton /> : <DesktopShellSkeleton />;
}

function MobileShellSkeleton() {
  return (
    <div className={skeletonStyles.mobileShell} data-testid="app-shell-skeleton">
      {/* Identity header: avatar + active-tab title */}
      <header className={skeletonStyles.mobileHeader}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton width={120} height={28} />
      </header>

      {/* Content: hero + two stacked cards */}
      <div className={skeletonStyles.mobileContent}>
        <Skeleton height={176} borderRadius="var(--radius-lg)" />
        <Skeleton height={100} borderRadius="var(--radius-md)" />
        <Skeleton height={100} borderRadius="var(--radius-md)" />
      </div>

      {/* Bottom nav: four icon+label tab stubs */}
      <nav className={skeletonStyles.mobileNav}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={skeletonStyles.mobileNavItem}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton width={36} height={10} />
          </div>
        ))}
      </nav>
    </div>
  );
}

function DesktopShellSkeleton() {
  return (
    <div className={skeletonStyles.shellContainer} data-testid="app-shell-skeleton">
      <aside className={skeletonStyles.sidebar}>
        <div className={skeletonStyles.logoContainer}>
          <Skeleton width={128} height={36} />
        </div>
        <nav className={skeletonStyles.nav}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={skeletonStyles.navItem}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton height={20} style={{ flex: 1 }} />
            </div>
          ))}
        </nav>
        <div className={skeletonStyles.footer}>
          <Skeleton width="100%" height={40} />
          <div className={skeletonStyles.userMenu}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton width={100} height={20} />
          </div>
        </div>
      </aside>

      <main className={skeletonStyles.main}>
        <header className={skeletonStyles.header}>
          <Skeleton width={120} height={32} />
          <Skeleton width={150} height={24} />
        </header>
        <div className={skeletonStyles.content}>
          <div className={skeletonStyles.contentRow}>
            <Skeleton width="60%" height={40} />
            <div className={skeletonStyles.contentGrid}>
              <Skeleton height={200} />
              <Skeleton height={200} />
              <Skeleton height={200} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
